type PostHogEventLike = {
  event?: unknown;
  properties?: Record<string, unknown>;
};

const UNACTIONABLE_EXCEPTION_PATTERNS = [
  /^script error\.?$/i,
  /^event captured as exception with keys: istrusted$/i,
  /^non-error thrown: \[object event\]$/i,
  /^resizeobserver loop (completed with undelivered notifications|limit exceeded)\.?$/i,
] as const;

/**
 * Bridges an in-app browser injects into whatever page it is hosting.
 *
 * Neither one exists in a real browser or in our own WebView, so on the web
 * they can only be a host app talking to itself: iOS in-app browsers reach for
 * `window.webkit.messageHandlers`, and Android ones (Meta's, chiefly) post
 * measurements to a `@JavascriptInterface` object — `sendDataToNative` /
 * `sendJsBlockingTimeMessage` frames, which throw "Java object is gone" once
 * the host has released the WebView mid-navigation. The page cannot keep that
 * object alive and does not own the code that calls it.
 *
 * Inside our own Capacitor shell the same failures would mean our bridge is
 * broken, so these only drop on the web.
 */
const WEB_ONLY_INJECTED_BRIDGE_PATTERNS = [
  /window\.webkit\.messageHandlers/,
  /error invoking \w+: java object is gone/i,
] as const;

function exceptionValues(event: PostHogEventLike): string[] {
  const properties = event.properties;
  if (!properties) return [];

  const values = properties.$exception_values;
  if (Array.isArray(values)) {
    return values.filter((value): value is string => typeof value === "string");
  }

  const list = properties.$exception_list;
  if (!Array.isArray(list)) return [];
  return list.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const value = (entry as { value?: unknown }).value;
    return typeof value === "string" ? [value] : [];
  });
}

/** Returns the first PostHog exception that can use the stale-load recovery. */
export function getPostHogExceptionError(event: unknown): Error | null {
  if (!event || typeof event !== "object") return null;
  const candidate = event as PostHogEventLike;
  if (candidate.event !== "$exception") return null;

  const value = exceptionValues(candidate).at(0);
  return value ? new Error(value) : null;
}

/**
 * Drop browser signals that contain no app stack and cannot be acted on.
 * These are generated by opaque cross-origin scripts or browser WebViews,
 * not by a failing Hiive code path.
 */
export function shouldDropPostHogClientEvent(
  event: unknown,
  isNativeWebView: boolean,
): boolean {
  if (!event || typeof event !== "object") return false;
  const candidate = event as PostHogEventLike;
  if (candidate.event !== "$exception") return false;

  const values = exceptionValues(candidate);
  if (values.length === 0) return false;

  return values.every(
    (value) =>
      UNACTIONABLE_EXCEPTION_PATTERNS.some((pattern) => pattern.test(value)) ||
      (!isNativeWebView &&
        WEB_ONLY_INJECTED_BRIDGE_PATTERNS.some((pattern) =>
          pattern.test(value),
        )),
  );
}

export type FailedResourceTag = "img" | "script" | "link" | "video" | "audio";

/**
 * Only a failed first-party Next.js script or stylesheet makes the app itself
 * incoherent. Media has an in-product fallback, while analytics/payment
 * vendors own their own retry and user-facing failure paths.
 */
export function getCriticalNextAssetError(input: {
  tag: FailedResourceTag;
  url: string;
  origin: string;
}): Error | null {
  if (input.tag !== "script" && input.tag !== "link") return null;

  try {
    const resource = new URL(input.url, input.origin);
    if (
      resource.origin !== input.origin ||
      !resource.pathname.startsWith("/_next/static/")
    ) {
      return null;
    }

    return new Error(
      `ChunkLoadError: Failed to load Next.js asset ${resource.pathname}`,
    );
  } catch {
    return null;
  }
}
